import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Input } from '@/components/ui/input';
import { HOTKEYS } from '@/lib/hotkeys';
import { cn } from '@/lib/utils';
import { CommandEmpty, Command as CommandPrimitive } from 'cmdk';
import { isEmpty } from 'lodash';
import { Loader2, SearchIcon } from 'lucide-react';
import { useState } from 'react';

type Props<T extends string> = {
  onSelectedValueChange: (value: T) => void;
  searchValue: string;
  onSearchValueChange: (value: string) => void;
  items: { value: T; label: string }[];
  isLoading?: boolean;
  emptyMessage?: string;
  placeholder?: string;
};

const WIDTH_CLASS = 'w-full';

export function AutoComplete<T extends string>({
  onSelectedValueChange,
  searchValue,
  onSearchValueChange,
  items,
  isLoading,
  emptyMessage = 'No items.',
  placeholder = 'Search...',
}: Props<T>) {
  const [open, setOpen] = useState(false);

  const onSelectItem = (inputValue: string) => {
    onSelectedValueChange(inputValue as T);
  };

  return (
    <div className={cn('flex items-start min-h-[300px]', WIDTH_CLASS)}>
      <Command shouldFilter={false}>
        <CommandPrimitive.Input
          asChild
          value={searchValue}
          onValueChange={onSearchValueChange}
          onKeyDown={(e) => setOpen(e.key !== HOTKEYS.GLOBAL_UNFOCUS_OR_CLOSE)}
          onMouseDown={() => setOpen((open) => !!searchValue || !open)}
          onFocus={() => setOpen(true)}
        >
          <div className="mx-2 flex flex-row items-center gap-2 justify-center">
            {isLoading ? (
              <Loader2 className="size-4 animate-spin text-gray-500" />
            ) : (
              <SearchIcon className="size-4 text-gray-500" />
            )}
            <Input placeholder={placeholder} />
          </div>
        </CommandPrimitive.Input>

        {!open && <CommandList aria-hidden="true" className="hidden" />}

        <CommandList
          className={cn(WIDTH_CLASS, {
            hidden: isEmpty(searchValue) || isLoading,
          })}
        >
          <CommandGroup>
            {items.map((option) => (
              <CommandItem
                key={option.value}
                value={option.value}
                onMouseDown={(e) => e.preventDefault()}
                onSelect={onSelectItem}
                className={cn(
                  'flex flex-row items-center gap-2 flex-nowrap text-nowrap',
                  WIDTH_CLASS,
                )}
              >
                {option.label}
              </CommandItem>
            ))}

            <CommandGroup>
              {!isLoading && !isEmpty(searchValue) && (
                <CommandEmpty className={cn(WIDTH_CLASS)}>
                  {emptyMessage ?? 'No items.'}
                </CommandEmpty>
              )}
            </CommandGroup>
          </CommandGroup>
        </CommandList>
      </Command>
    </div>
  );
}
